import { produce } from 'immer';
import debounce from 'just-debounce-it';
import { Loader2, Sparkles, X } from 'lucide-react';
import { Fragment, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import FadeIn from '@/components/animations/FadeIn';
import ButtonGroup from '@/components/ButtonGroup/ButtonGroup';
import TipTapEditorV3 from '@/components/TipTapEditorV3/TipTapEditorV3';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { CourseResourceEnum, EditorType } from '@/Enums/enum';
import useAiSuggest, { type ResourceSuggestion } from '@/hooks/useAiSuggest';
import useSaveDraft from '@/hooks/useSaveDraft';
import { TrashCanIcon } from '@/icons/index';
import { StyledTextArea } from '@/Shared/StyledElements';
import { StyledH5 } from '@/Shared/Typography/typography';
import { useCourseEditStore } from '@/stores/courseEditStore';

import CourseDiff from '../CourseDiff';
import CourseEditContext from '../CourseEditContext';

import CourseResourcePlaceholder from './CourseResourcePlaceholder';
import {
	StyledActionGroup,
	StyledForm,
	StyledResource,
	StyledResourceGroup,
} from './CourseResourcesTab.Styles';

const StyledTipTapEditorV3 = styled(TipTapEditorV3)`
	margin-bottom: var(--spacing-6);
`;

const CourseResourcesTab = () => {
	const {
		courseData,
		removeResource,
		addResource,
		addResourceFromSuggestion,
		updateResources,
		canEdit,
		saveDraft,
		submitCourse,
		updateResourceInfo,
		courseDraftData,
		isApprovalMode,
		updateCourseReducer,
	} = useContext(CourseEditContext);
	const { courses_resources: coursesResources, course_resource_info: courseResourceInfo } =
		courseData;
	const {
		courses_resources: coursesDraftResources,
		course_resource_info: courseDraftResourceInfo,
	} = courseDraftData;
	const { updateCommentTypes } = useCourseEditStore();

	const ref = useRef(null);
	const inputFocusRef = useRef(false);
	const { register, handleSubmit, watch } = useForm({
		shouldUnregister: true,
	});
	const initialType = { show: CourseResourceEnum.All };

	const typeReducer = (
		_state: { show: CourseResourceEnum },
		action: { type: CourseResourceEnum }
	) => {
		switch (action.type) {
			case CourseResourceEnum.Media:
				return { show: CourseResourceEnum.Media };
			case CourseResourceEnum.Textbook:
				return { show: CourseResourceEnum.Textbook };
			case CourseResourceEnum.Info:
				return { show: CourseResourceEnum.Info };
			default:
				return { show: CourseResourceEnum.All };
		}
	};

	const [state, dispatch] = useReducer(typeReducer, initialType);
	const handleResourcesUpdate = debounce(() => {
		const watchAllFields = watch();
		const formEntries = Object.entries(watchAllFields);

		// Group form entries by resource ID (extracted from the parent field value)
		const resourceUpdates: Record<
			string,
			Partial<CoursesResourcesEntity['resource']> & { course_resources_id?: string }
		> = {};

		// First pass: extract course_resources_id from parent fields
		// Field names are like "media-parent-{uuid}" where uuid may contain hyphens
		formEntries.forEach((entry: string[]) => {
			const [prop, val] = entry;
			if (prop.includes('-parent-')) {
				// The value of the parent field IS the course_resources_id
				const courseResourceId = val;
				if (courseResourceId && !resourceUpdates[courseResourceId]) {
					resourceUpdates[courseResourceId] = { course_resources_id: courseResourceId };
				}
			}
		});

		// Second pass: populate the resource updates using the course_resources_id
		formEntries.forEach((entry: string[]) => {
			const [prop, val] = entry;

			// Find which resource this field belongs to by checking if the field name ends with any known ID
			const matchingResourceId = Object.keys(resourceUpdates).find((id) => prop.endsWith(id));
			if (!matchingResourceId) return;

			const isMedia = prop.startsWith('media-');

			if (prop.includes('-id-')) {
				resourceUpdates[matchingResourceId].resource_id = val;
				resourceUpdates[matchingResourceId].resource_type = isMedia ? 'media' : 'textbook';
			}
			if (prop.includes('-author-')) {
				resourceUpdates[matchingResourceId].resource_author = val;
			}
			if (prop.includes('-source-') || prop.includes('-description-')) {
				resourceUpdates[matchingResourceId].resource_detail = val;
			}
			if (prop.includes('-title-')) {
				resourceUpdates[matchingResourceId].resource_title = val;
			}
			if (prop.includes('-isbn-')) {
				resourceUpdates[matchingResourceId].resource_isbn = val;
			}
		});

		// Update resources immutably by ID, preserving original order
		const updatedResources = produce(coursesDraftResources ?? [], (draft) => {
			for (const [courseResourceId, updates] of Object.entries(resourceUpdates)) {
				const existingResource = draft.find((r) => r.course_resources_id === courseResourceId);
				if (existingResource && updates.course_resources_id) {
					if (updates.resource_author !== undefined)
						existingResource.resource.resource_author = updates.resource_author;
					if (updates.resource_detail !== undefined)
						existingResource.resource.resource_detail = updates.resource_detail;
					if (updates.resource_title !== undefined)
						existingResource.resource.resource_title = updates.resource_title;
					if (updates.resource_isbn !== undefined)
						existingResource.resource.resource_isbn = updates.resource_isbn;
					if (updates.resource_type !== undefined)
						existingResource.resource.resource_type = updates.resource_type;
				}
			}
		});

		updateResources(updatedResources);
	}, 500);

	const onResourceChange = debounce((value: string) => {
		updateResourceInfo(value);
	}, 500);

	const [isCommented, setIsCommented] = useState(false);
	// once a comment is added, state is updated and the draft is saved
	const saveAfterCommenting = (value: string) => {
		updateCourseReducer(value, EditorType.RESOURCES);
		setIsCommented(!isCommented);
	};
	// once a comment is added, save a draft using hook
	useSaveDraft(isCommented, saveDraft, setIsCommented);

	const {
		suggest: suggestResources,
		loading: resourcesAiLoading,
		suggestions: aiResourceSuggestions,
		panelOpen: resourcesAiPanelOpen,
		setPanelOpen: setResourcesAiPanelOpen,
		markAccepted: markResourcesAccepted,
		error: resourcesAiError,
	} = useAiSuggest('suggest_resources');

	const [checkedResources, setCheckedResources] = useState<Record<number, boolean>>({});

	useEffect(() => {
		const initial: Record<number, boolean> = {};
		aiResourceSuggestions.forEach((_, i) => {
			initial[i] = true;
		});
		setCheckedResources(initial);
	}, [aiResourceSuggestions]);

	const handleAddResources = () => {
		aiResourceSuggestions.forEach((s, i) => {
			if (!checkedResources[i]) return;
			const res = s as ResourceSuggestion;
			const isMedia = res.type === 'Media';
			addResourceFromSuggestion({
				resourceType: isMedia ? CourseResourceEnum.Media : CourseResourceEnum.Textbook,
				title: res.title,
				// For media: source label (e.g. "Documentary series on PBS") goes into author field
				// For textbooks: author name goes into author field
				author: isMedia ? res.source : res.author,
				detail: undefined,
				isbn: res.isbn,
			});
		});
		markResourcesAccepted();
		setResourcesAiPanelOpen(false);
	};

	const setFilterType = (type: string) =>
		coursesDraftResources?.filter(
			({ resource }: CoursesResourcesEntity) => resource.resource_type === type
		) ?? [];

	const publishedMedia =
		coursesResources?.filter(
			({ resource }: CoursesResourcesEntity) => resource.resource_type === CourseResourceEnum.Media
		) ?? [];

	const publishedTextbook =
		coursesResources?.filter(
			({ resource }: CoursesResourcesEntity) =>
				resource.resource_type === CourseResourceEnum.Textbook
		) ?? [];

	const handleFilter = (type: CourseResourceEnum) => {
		dispatch({ type });
	};

	const setNewResource = (type: string) => {
		addResource(type);
		inputFocusRef.current = true;
	};

	const showButtonType = (type: string) =>
		state.show === type || state.show === CourseResourceEnum.All;

	const onSubmit = () => submitCourse();

	const renderMediaDiffContent = (content: CoursesResourcesEntity | undefined) => {
		if (!content) return '';
		return `Title: ${content.resource.resource_title ?? ''} </br> Author: ${
			content.resource.resource_author ?? ''
		} </br> Source:  ${content.resource.resource_detail ?? ''}`;
	};

	const renderTextbookDiffContent = (content: CoursesResourcesEntity | undefined) => {
		if (!content) return '';
		return `Title: ${content.resource.resource_title ?? ''} </br> ISBN: ${
			content.resource.resource_isbn ?? ''
		} </br> Author:  ${content.resource.resource_author ?? ''}  </br> Description:  ${
			content.resource.resource_detail ?? ''
		}`;
	};

	useEffect(() => {
		updateCommentTypes([EditorType.RESOURCES]);
	}, [updateCommentTypes]);

	return (
		<>
			<div className="flex items-center justify-between mb-4">
				<ButtonGroup filterEvent={handleFilter} types={CourseResourceEnum} />
				{canEdit && !isApprovalMode && (
					<Button
						type="button"
						variant="secondary"
						size="xs"
						onClick={suggestResources}
						disabled={resourcesAiLoading}
					>
						{resourcesAiLoading ? (
							<Loader2 size={12} className="animate-spin mr-0.5" />
						) : (
							<Sparkles size={12} className="mr-0.5" />
						)}
						{resourcesAiLoading ? 'Suggesting…' : 'Suggest Resources'}
					</Button>
				)}
			</div>

			{/* AI error */}
			{resourcesAiError && <p className="text-[12px] text-destructive mb-4">{resourcesAiError}</p>}

			{/* AI resource suggestions */}
			{resourcesAiPanelOpen && (
				<div className="rounded-lg border border-border bg-secondary/40 p-4 mb-5">
					<div className="flex items-center justify-between mb-1">
						<div className="flex items-center gap-2 text-[13.5px] font-semibold text-primary">
							<Sparkles size={13} />
							AI-suggested resources
						</div>
						<div className="flex items-center gap-2">
							<Button type="button" size="xs" onClick={handleAddResources}>
								Add selected
							</Button>
							<button
								type="button"
								onClick={() => setResourcesAiPanelOpen(false)}
								className="text-muted-foreground hover:text-foreground transition-colors p-1"
								aria-label="Discard suggestions"
							>
								<X size={14} />
							</button>
						</div>
					</div>
					<p className="text-[12px] text-muted-foreground pb-2">
						These suggestions are AI-generated based on your course description. We recommend
						reviewing titles, authors, and ISBNs before publishing — details may occasionally need a
						quick confirmation.
					</p>
					<div className="flex flex-col gap-1.5">
						{aiResourceSuggestions.map((s, i) => {
							const resource = s as ResourceSuggestion;
							return (
								<label
									key={resource.title ?? i}
									htmlFor={`resource-suggestion-${i}`}
									className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-left text-[13px] transition-colors cursor-pointer ${
										checkedResources[i]
											? 'border-border bg-white text-foreground'
											: 'border-transparent bg-white/50 text-muted-foreground'
									}`}
								>
									<Checkbox
										id={`resource-suggestion-${i}`}
										checked={!!checkedResources[i]}
										className="mt-0.5 shrink-0"
										onCheckedChange={() =>
											setCheckedResources((prev) => ({ ...prev, [i]: !prev[i] }))
										}
									/>
									<div>
										<span className="font-medium">{resource.title}</span>
										<span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground border border-border mb-0.5 inline-block">
											{resource.type}
										</span>
										<p className="text-[13px] text-muted-foreground mt-0.5">
											{resource.type === 'Media'
												? resource.source
												: `${resource.author ?? ''}${resource.isbn ? ` · ISBN ${resource.isbn}` : ''}`}
										</p>
									</div>
								</label>
							);
						})}
					</div>
				</div>
			)}

			<FadeIn style={{ width: '100%' }}>
				<div style={{ width: '100%' }}>
					<StyledForm onSubmit={handleSubmit(onSubmit)} ref={ref} onChange={handleResourcesUpdate}>
						{[CourseResourceEnum.Info, CourseResourceEnum.All].includes(state.show) && (
							<>
								{!isApprovalMode && (
									<StyledTipTapEditorV3
										content={courseDraftResourceInfo}
										onChange={onResourceChange}
										placeholder="Add resource additional information..."
										type={EditorType.RESOURCES}
										isEditable={canEdit || !isApprovalMode}
										id={courseData.course_id}
										commentAddedCallback={saveAfterCommenting}
									/>
								)}
								<CourseDiff
									title="Resource Additional Info"
									original={courseResourceInfo}
									draft={courseDraftResourceInfo}
								/>
							</>
						)}
						{[CourseResourceEnum.Media, CourseResourceEnum.All].includes(state.show) && (
							<StyledResourceGroup>
								{setFilterType(CourseResourceEnum.Media).map(
									(resource: CoursesResourcesEntity, i: number) => {
										const { course_resources_id: courseResourceId } = resource;
										const {
											resource_author: resourceAuthor,
											resource_detail: resourceDetail,
											resource_title: resourceTitle,
											resource_id: resourceId,
										} = resource.resource;

										return (
											<Fragment key={courseResourceId}>
												{i === 0 ? <StyledH5 mb="16px">Media</StyledH5> : null}
												<StyledResource data-testid={`media-${i}`}>
													<div className="input-wrap">
														{!isApprovalMode && (
															<Input
																type="text"
																defaultValue={resourceTitle}
																placeholder="Title"
																readOnly={!canEdit || isApprovalMode}
																autoFocus={inputFocusRef.current}
																{...register(`media-title-${courseResourceId}`)}
															/>
														)}
														<CourseDiff
															title="Title"
															original={renderMediaDiffContent(
																publishedMedia.find(
																	(r) => r.course_resources_id === courseResourceId
																) as CoursesResourcesEntity
															)}
															draft={renderMediaDiffContent(resource)}
														/>
														{!isApprovalMode && (
															<Input
																type="text"
																defaultValue={resourceAuthor}
																placeholder="Author"
																readOnly={!canEdit || isApprovalMode}
																{...register(`media-author-${courseResourceId}`)}
															/>
														)}
														{!isApprovalMode && (
															<Input
																type="url"
																defaultValue={resourceDetail}
																placeholder="Source"
																readOnly={!canEdit || isApprovalMode}
																{...register(`media-source-${courseResourceId}`)}
															/>
														)}
														{!isApprovalMode && (
															<>
																<input
																	readOnly
																	hidden
																	value={resourceId}
																	{...register(`media-id-${courseResourceId}`)}
																/>
																<input
																	readOnly
																	hidden
																	value={courseResourceId}
																	{...register(`media-parent-${courseResourceId}`)}
																/>
															</>
														)}
													</div>
													{canEdit && !isApprovalMode && (
														<TrashCanIcon passedEvent={() => removeResource(courseResourceId)} />
													)}
												</StyledResource>
											</Fragment>
										);
									}
								)}
							</StyledResourceGroup>
						)}

						{[CourseResourceEnum.Textbook, CourseResourceEnum.All].includes(state.show) && (
							<StyledResourceGroup>
								{setFilterType(CourseResourceEnum.Textbook).map(
									(resource: CoursesResourcesEntity, i: number) => {
										const { course_resources_id: courseResourceId } = resource;
										const {
											resource_author: resourceAuthor,
											resource_detail: resourceDetail,
											resource_title: resourceTitle,
											resource_isbn: resourceISBN,
											resource_id: resourceId,
										} = resource.resource;

										return (
											<Fragment key={courseResourceId}>
												{i === 0 ? <StyledH5 mb="16px">Textbooks</StyledH5> : null}
												<StyledResource data-testid={`textbook-${i}`}>
													<div className="input-wrap">
														{!isApprovalMode && (
															<Input
																type="text"
																defaultValue={resourceTitle}
																placeholder="Title"
																readOnly={!canEdit || isApprovalMode}
																{...register(`textbook-title-${courseResourceId}`)}
																autoFocus={inputFocusRef.current}
															/>
														)}
														<CourseDiff
															title="Title"
															original={renderTextbookDiffContent(
																publishedTextbook.find(
																	(r) => r.course_resources_id === courseResourceId
																) as CoursesResourcesEntity
															)}
															draft={renderTextbookDiffContent(resource)}
														/>
														{!isApprovalMode && (
															<Input
																type="text"
																defaultValue={resourceISBN}
																placeholder="ISBN"
																readOnly={!canEdit || isApprovalMode}
																{...register(`textbook-isbn-${courseResourceId}`)}
															/>
														)}
														{!isApprovalMode && (
															<Input
																type="text"
																defaultValue={resourceAuthor}
																placeholder="Author"
																readOnly={!canEdit || isApprovalMode}
																{...register(`textbook-author-${courseResourceId}`)}
															/>
														)}
														{!isApprovalMode && (
															<StyledTextArea
																readOnly={!canEdit || isApprovalMode}
																defaultValue={resourceDetail}
																placeholder="Description"
																{...register(`textbook-description-${courseResourceId}`)}
															/>
														)}
														{!isApprovalMode && (
															<>
																<input
																	readOnly
																	hidden
																	value={resourceId}
																	{...register(`textbook-id-${courseResourceId}`)}
																/>
																<input
																	readOnly
																	hidden
																	value={courseResourceId}
																	{...register(`textbook-parent-${courseResourceId}`)}
																/>
															</>
														)}
													</div>
													{canEdit && !isApprovalMode && (
														<TrashCanIcon passedEvent={() => removeResource(courseResourceId)} />
													)}
												</StyledResource>
											</Fragment>
										);
									}
								)}
							</StyledResourceGroup>
						)}
					</StyledForm>
					<CourseResourcePlaceholder
						isApprovalMode={isApprovalMode}
						state={state}
						textbookLength={setFilterType(CourseResourceEnum.Textbook).length}
						mediaLength={setFilterType(CourseResourceEnum.Media).length}
					/>
				</div>
				{canEdit && !isApprovalMode && (
					<StyledActionGroup>
						{showButtonType(CourseResourceEnum.Media) && (
							<Button
								variant="secondary"
								size="small"
								onClick={() => setNewResource(CourseResourceEnum.Media)}
							>
								Add New Media
							</Button>
						)}
						{showButtonType(CourseResourceEnum.Textbook) && (
							<Button
								variant="secondary"
								size="small"
								onClick={() => setNewResource(CourseResourceEnum.Textbook)}
							>
								Add New Textbook
							</Button>
						)}
					</StyledActionGroup>
				)}
			</FadeIn>
		</>
	);
};

export default CourseResourcesTab;
